import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableHead, TableRow, TableCell, Checkbox, Button, Chip, CircularProgress,
  Alert, Snackbar, Tooltip, Divider, Stack,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { useQuery, useMutation } from '@apollo/client';
import {
  ROLES_QUERY,
  ALL_ROLE_PAGE_PERMISSIONS_QUERY,
  BULK_SET_ROLE_PAGE_PERMISSIONS_MUTATION,
} from '../graphql/queries';

// ── Page/Module definitions ───────────────────────────────────
const PAGES = [
  // General
  { key: 'dashboard_overview', label: 'Dashboard Overview',  group: 'General' },
  { key: 'profile',            label: 'My Profile',          group: 'General' },
  { key: 'matchmaking',        label: 'Matchmaking',         group: 'General' },
  { key: 'messages',           label: 'Messages',            group: 'General' },
  { key: 'meetings',           label: 'Meetings',            group: 'General' },
  { key: 'contracts',          label: 'Contracts',           group: 'General' },
  { key: 'wishlist',           label: 'Wish List',           group: 'General' },
  { key: 'wishlist_wizard',    label: 'Service Wizard',      group: 'General' },
  // Phase 2
  { key: 'rituals',            label: 'Ritual Planning',     group: 'Phase 2 – Planning' },
  { key: 'ritual_items',       label: 'Ritual Items',        group: 'Phase 2 – Planning' },
  { key: 'venues',             label: 'Venues',              group: 'Phase 2 – Planning' },
  { key: 'vendors',            label: 'Vendors',             group: 'Phase 2 – Planning' },
  { key: 'service_providers',  label: 'Service Providers',   group: 'Phase 2 – Planning' },
  { key: 'my_services',        label: 'My Services (vendor)', group: 'General' },
  { key: 'vendor_bookings',    label: 'Bookings (vendor)',    group: 'General' },
  { key: 'my_vendors',         label: 'My Vendors',          group: 'Phase 2 – Planning' },
  // Phase 3
  { key: 'catering',                 label: 'Catering & Menu Planning',  group: 'Phase 3 – Experience' },
  { key: 'guest_list',               label: 'Guest List',                group: 'Phase 3 – Experience' },
  { key: 'transportation',           label: 'Transportation',             group: 'Phase 3 – Experience' },
  { key: 'entertainment',            label: 'Entertainment Programs',     group: 'Phase 3 – Experience' },
  { key: 'staffing',                 label: 'Staffing',                  group: 'Phase 3 – Experience' },
  { key: 'parking',                  label: 'Parking & Valet',           group: 'Phase 3 – Experience' },
  { key: 'decoration',               label: 'Decoration Themes',         group: 'Phase 3 – Experience' },
  { key: 'seating',                  label: 'Seating Arrangements',      group: 'Phase 3 – Experience' },
  { key: 'vip_escorts',              label: 'VIP Escorts',               group: 'Phase 3 – Experience' },
  { key: 'caterer_comparison',       label: 'Caterer Comparison',        group: 'Phase 3 – Experience' },
  // Phase 4
  { key: 'timeline',                 label: 'Timeline',                  group: 'Phase 4 – Planning & Comms' },
  { key: 'budget',                   label: 'Budget',                    group: 'Phase 4 – Planning & Comms' },
  { key: 'notifications',            label: 'Notifications',             group: 'Phase 4 – Planning & Comms' },
  { key: 'communication',            label: 'Communication',             group: 'Phase 4 – Planning & Comms' },
  // Phase 5
  { key: 'honeymoon',                label: 'Honeymoon Planning',        group: 'Phase 5 – Post Wedding' },
  { key: 'photo_albums',             label: 'Photo Albums',              group: 'Phase 5 – Post Wedding' },
  { key: 'invitations',              label: 'Invitations',               group: 'Phase 5 – Post Wedding' },
  { key: 'accommodation',            label: 'Accommodation',             group: 'Phase 5 – Post Wedding' },
  { key: 'gift_registry',            label: 'Gift Registry',             group: 'Phase 5 – Post Wedding' },
  { key: 'beauty_services',          label: 'Beauty Services',           group: 'Phase 5 – Post Wedding' },
  { key: 'astrology',                label: 'Astrology',                 group: 'Phase 5 – Post Wedding' },
  // Admin
  { key: 'admin_users',              label: 'Admin: Users',              group: 'Admin' },
  { key: 'admin_roles',              label: 'Admin: Roles',              group: 'Admin' },
  { key: 'admin_subscriptions',      label: 'Admin: Subscriptions',      group: 'Admin' },
  { key: 'admin_permissions',        label: 'Admin: Permissions',        group: 'Admin' },
  { key: 'admin_permissions_crud',   label: 'Admin: Permissions CRUD',   group: 'Admin' },
  { key: 'admin_lov',                label: 'Admin: List of Values',     group: 'Admin' },
  { key: 'admin_contracts',          label: 'Admin: Contracts',          group: 'Admin' },
  { key: 'admin_services',           label: 'Admin: Services',           group: 'Admin' },
  { key: 'admin_venues',             label: 'Admin: Venues',             group: 'Admin' },
  { key: 'admin_vendors',            label: 'Admin: Vendors',            group: 'Admin' },
  { key: 'admin_ritual_items',       label: 'Admin: Ritual Items',       group: 'Admin' },
  { key: 'admin_caterers',           label: 'Admin: Caterers',           group: 'Admin' },
  { key: 'admin_entertainment_acts', label: 'Admin: Entertainment Acts', group: 'Admin' },
  { key: 'admin_honeymoon',          label: 'Admin: Honeymoon',          group: 'Admin' },
  { key: 'admin_beauty_services',    label: 'Admin: Beauty Services',    group: 'Admin' },
  { key: 'admin_invitations',        label: 'Admin: Invitations',        group: 'Admin' },
  { key: 'admin_astrology',          label: 'Admin: Astrology',          group: 'Admin' },
  { key: 'admin_wishlist_templates', label: 'Admin: Wishlist Templates', group: 'Admin' },
  { key: 'admin_wishlist_approvals', label: 'Admin: Wishlist Approvals', group: 'Admin' },
  { key: 'admin_decoration_themes',  label: 'Admin: Decoration Themes',  group: 'Admin' },
  { key: 'admin_staffing',           label: 'Admin: Staffing',           group: 'Admin' },
  { key: 'admin_transportation',     label: 'Admin: Transportation',     group: 'Admin' },
  { key: 'admin_parking',            label: 'Admin: Parking',            group: 'Admin' },
  { key: 'admin_timeline',           label: 'Admin: Timeline',           group: 'Admin' },
  { key: 'admin_budget_categories',  label: 'Admin: Budget Categories',  group: 'Admin' },
  { key: 'admin_notifications',      label: 'Admin: Notifications',      group: 'Admin' },
  { key: 'admin_notification_triggers', label: 'Admin: Event Triggers', group: 'Admin' },
  { key: 'admin_service_categories', label: 'Admin: Service Categories', group: 'Admin' },
  { key: 'admin_email_settings',     label: 'Admin: Email Settings',     group: 'Admin' },
  { key: 'admin_email_templates',    label: 'Admin: Email Templates',    group: 'Admin' },
  { key: 'admin_company',            label: 'Admin: Company Details',    group: 'Admin' },
  { key: 'admin_questions',          label: 'Admin: Service Questions',  group: 'Admin' },
];

const ACTIONS = [
  { key: 'canRead',   label: 'View',   color: '#2196f3' },
  { key: 'canCreate', label: 'Create', color: '#4caf50' },
  { key: 'canUpdate', label: 'Update', color: '#ff9800' },
  { key: 'canDelete', label: 'Delete', color: '#f44336' },
];

// Build a flat permission map: { [roleId]: { [page]: { canRead, canCreate, canUpdate, canDelete } } }
function buildPermMap(rows) {
  const map = {};
  rows.forEach(r => {
    if (!map[r.roleId]) map[r.roleId] = {};
    map[r.roleId][r.page] = {
      canCreate: r.canCreate,
      canRead:   r.canRead,
      canUpdate: r.canUpdate,
      canDelete: r.canDelete,
    };
  });
  return map;
}

// Default permission matrix per role
function defaultPerms(roleName, page) {
  const isAdmin = roleName === 'system_admin';
  const isAdminPage = page.startsWith('admin_');
  const isPhase3Page = ['catering','guest_list','transportation','entertainment','staffing','parking','decoration','seating','vip_escorts','caterer_comparison'].includes(page);
  const isPhase2Page = ['rituals','ritual_items','venues','vendors'].includes(page);
  const isPhase4Page = ['timeline','budget','notifications','communication'].includes(page);
  const isPhase5Page = ['honeymoon','photo_albums','invitations','accommodation','gift_registry','beauty_services','astrology'].includes(page);

  if (isAdmin) return { canRead: true, canCreate: true, canUpdate: true, canDelete: true };
  if (isAdminPage) return { canRead: false, canCreate: false, canUpdate: false, canDelete: false };
  // matchmaker – read all, full CRUD on Phase 2/3 management pages
  if (roleName === 'matchmaker') {
    if (isPhase3Page || isPhase2Page || isPhase4Page || isPhase5Page) return { canRead: true, canCreate: true, canUpdate: true, canDelete: false };
    return { canRead: true, canCreate: false, canUpdate: false, canDelete: false };
  }
  // Regular members: full CRUD on own pages, read-only on matchmaking
  if (page === 'matchmaking') return { canRead: true, canCreate: false, canUpdate: false, canDelete: false };
  if (isPhase2Page || isPhase3Page || isPhase4Page || isPhase5Page) return { canRead: true, canCreate: true, canUpdate: true, canDelete: true };
  return { canRead: true, canCreate: true, canUpdate: true, canDelete: false };
}

export default function AdminPermissionsPage() {
  const [activeRoleIdx, setActiveRoleIdx] = useState(0);
  const [localMap, setLocalMap] = useState({});   // { [roleId]: { [page]: { ... } } }
  const [dirtyRoles, setDirtyRoles] = useState(new Set());
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const { data: rolesData, loading: rolesLoading } = useQuery(ROLES_QUERY);
  const { data: permData, loading: permLoading, refetch } = useQuery(ALL_ROLE_PAGE_PERMISSIONS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [bulkSave, { loading: saving }] = useMutation(BULK_SET_ROLE_PAGE_PERMISSIONS_MUTATION);

  const roles = rolesData?.roles || [];

  // Initialise localMap whenever server data or roles load
  useEffect(() => {
    if (!roles.length) return;
    const serverMap = buildPermMap(permData?.allRolePagePermissions || []);
    const init = {};
    roles.forEach(role => {
      init[role.id] = {};
      PAGES.forEach(p => {
        init[role.id][p.key] = serverMap[role.id]?.[p.key] ?? defaultPerms(role.name, p.key);
      });
    });
    setLocalMap(init);
    setDirtyRoles(new Set());
  }, [permData, roles.length]); // eslint-disable-line

  const activeRole = roles[activeRoleIdx];

  const toggle = useCallback((roleId, page, action) => {
    setLocalMap(prev => ({
      ...prev,
      [roleId]: {
        ...prev[roleId],
        [page]: {
          ...prev[roleId]?.[page],
          [action]: !prev[roleId]?.[page]?.[action],
        },
      },
    }));
    setDirtyRoles(prev => new Set([...prev, roleId]));
  }, []);

  const toggleAll = useCallback((roleId, page, checked) => {
    setLocalMap(prev => ({
      ...prev,
      [roleId]: {
        ...prev[roleId],
        [page]: { canRead: checked, canCreate: checked, canUpdate: checked, canDelete: checked },
      },
    }));
    setDirtyRoles(prev => new Set([...prev, roleId]));
  }, []);

  const toggleAction = useCallback((roleId, action, checked) => {
    setLocalMap(prev => {
      const updated = { ...prev[roleId] };
      PAGES.forEach(p => { updated[p.key] = { ...updated[p.key], [action]: checked }; });
      return { ...prev, [roleId]: updated };
    });
    setDirtyRoles(prev => new Set([...prev, roleId]));
  }, []);

  const handleSave = async (roleId) => {
    const rolePerms = localMap[roleId];
    const permissions = PAGES.map(p => ({
      page:      p.key,
      canCreate: rolePerms[p.key]?.canCreate ?? false,
      canRead:   rolePerms[p.key]?.canRead   ?? false,
      canUpdate: rolePerms[p.key]?.canUpdate ?? false,
      canDelete: rolePerms[p.key]?.canDelete ?? false,
    }));
    try {
      await bulkSave({ variables: { roleId, permissions } });
      await refetch();
      setDirtyRoles(prev => { const s = new Set(prev); s.delete(roleId); return s; });
      setSnack({ open: true, msg: 'Permissions saved successfully!', severity: 'success' });
    } catch (e) {
      setSnack({ open: true, msg: e.message || 'Save failed', severity: 'error' });
    }
  };

  if (rolesLoading || permLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!roles.length) {
    return <Alert severity="warning" sx={{ m: 3 }}>No roles found.</Alert>;
  }

  const rolePerms = activeRole ? (localMap[activeRole.id] || {}) : {};
  const isDirty = activeRole ? dirtyRoles.has(activeRole.id) : false;

  // Per-page: are all 4 actions checked?
  const isPageAllChecked = (page) => {
    const p = rolePerms[page] || {};
    return p.canRead && p.canCreate && p.canUpdate && p.canDelete;
  };
  const isPagePartial = (page) => {
    const p = rolePerms[page] || {};
    const vals = [p.canRead, p.canCreate, p.canUpdate, p.canDelete];
    return vals.some(Boolean) && !vals.every(Boolean);
  };

  // Per-action: are all pages checked for that action?
  const isActionAllChecked = (action) =>
    PAGES.every(p => rolePerms[p.key]?.[action]);
  const isActionPartial = (action) => {
    const vals = PAGES.map(p => rolePerms[p.key]?.[action]);
    return vals.some(Boolean) && !vals.every(Boolean);
  };

  const groups = [...new Set(PAGES.map(p => p.group))];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Role Page Permissions</Typography>
          <Typography variant="body2" color="text.secondary">
            Assign Create / Read / Update / Delete access per role per page
          </Typography>
        </Box>
      </Stack>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        After saving, affected users must <strong>reload the page</strong> (or log out and back in) to see the updated navigation.
      </Alert>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Role Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Tabs
            value={activeRoleIdx}
            onChange={(_, v) => setActiveRoleIdx(v)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {roles.map((role, i) => (
              <Tab
                key={role.id}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{role.displayName || role.name}</span>
                    {dirtyRoles.has(role.id) && (
                      <Chip label="unsaved" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />
                    )}
                  </Stack>
                }
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Permission Matrix */}
        {activeRole && (
          <Box>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {activeRole.displayName || activeRole.name}
                <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                  — tick the operations each role can perform on each page
                </Typography>
              </Typography>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                disabled={!isDirty || saving}
                onClick={() => handleSave(activeRole.id)}
                color={isDirty ? 'primary' : 'success'}
              >
                {isDirty ? 'Save Changes' : 'Saved'}
              </Button>
            </Box>
            <Divider />

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'primary.main', color: 'white' } }}>
                    <TableCell sx={{ minWidth: 220, bgcolor: 'primary.dark !important' }}>
                      Page / Module
                    </TableCell>
                    {/* "All" column */}
                    <TableCell align="center" sx={{ width: 80 }}>
                      <Tooltip title="Toggle all actions for this page">
                        <span>All</span>
                      </Tooltip>
                    </TableCell>
                    {ACTIONS.map(a => (
                      <TableCell key={a.key} align="center" sx={{ width: 100 }}>
                        <Tooltip title={`Toggle ${a.label} for ALL pages`}>
                          <Stack alignItems="center" spacing={0.3}>
                            <Chip
                              label={a.label}
                              size="small"
                              sx={{
                                bgcolor: a.color,
                                color: 'white',
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontSize: 11,
                              }}
                            />
                            <Checkbox
                              size="small"
                              checked={isActionAllChecked(a.key)}
                              indeterminate={isActionPartial(a.key)}
                              onChange={(e) => toggleAction(activeRole.id, a.key, e.target.checked)}
                              sx={{ color: 'white', p: 0, '&.Mui-checked': { color: 'white' } }}
                            />
                          </Stack>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {groups.map(group => (
                    <React.Fragment key={group}>
                      {/* Group header row */}
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell colSpan={6} sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary', py: 0.5, pl: 2 }}>
                          {group.toUpperCase()}
                        </TableCell>
                      </TableRow>

                      {PAGES.filter(p => p.group === group).map((page, idx) => {
                        const p = rolePerms[page.key] || {};
                        const allChecked = isPageAllChecked(page.key);
                        const partial    = isPagePartial(page.key);
                        return (
                          <TableRow
                            key={page.key}
                            sx={{
                              bgcolor: idx % 2 === 0 ? 'white' : 'grey.50',
                              '&:hover': { bgcolor: 'primary.50' },
                            }}
                          >
                            <TableCell sx={{ pl: 3, fontWeight: 500 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                {allChecked && <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                                <span>{page.label}</span>
                              </Stack>
                            </TableCell>

                            {/* "All" toggle */}
                            <TableCell align="center">
                              <Tooltip title={allChecked ? 'Deselect all' : 'Select all'}>
                                <Checkbox
                                  size="small"
                                  checked={allChecked}
                                  indeterminate={partial}
                                  onChange={(e) => toggleAll(activeRole.id, page.key, e.target.checked)}
                                  color="primary"
                                />
                              </Tooltip>
                            </TableCell>

                            {ACTIONS.map(a => (
                              <TableCell key={a.key} align="center">
                                <Checkbox
                                  size="small"
                                  checked={!!p[a.key]}
                                  onChange={() => toggle(activeRole.id, page.key, a.key)}
                                  sx={{
                                    color: a.color,
                                    '&.Mui-checked': { color: a.color },
                                  }}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {/* Footer actions */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                disabled={!isDirty || saving}
                onClick={() => handleSave(activeRole.id)}
              >
                {saving ? 'Saving…' : isDirty ? 'Save Changes' : 'Saved'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Save-all dirty roles helper */}
      {dirtyRoles.size > 1 && (
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="secondary"
            onClick={async () => {
              for (const rid of dirtyRoles) await handleSave(rid);
            }}
          >
            Save All {dirtyRoles.size} Changed Roles
          </Button>
        </Box>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
